import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useReveal } from "@/hooks/useReveal";

const VALUES = [
  { icon: "ri-file-text-line", title: "内容详实", desc: "每部电影都配有清晰的剧情简介与基础资料，帮助观众快速了解作品。" },
  { icon: "ri-compasses-2-line", title: "浏览高效", desc: "多维度分类筛选与搜索，让你在最短时间找到想看的电影。" },
  { icon: "ri-heart-3-line", title: "面向观众", desc: "从影迷视角出发，梳理题材、地区与看点，让选片不再盲目。" },
  { icon: "ri-shield-check-line", title: "演示透明", desc: "本站为影视内容演示站，所有数据仅供展示，不含播放与下载功能。" },
];

const CONTACTS = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@mitaomovie.demo" },
  { icon: "ri-customer-service-2-line", label: "内容合作", value: "media@mitaomovie.demo" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "浙江省杭州市西湖区文创园 A 座 12 层" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周五 10:00 - 18:00" },
];

export default function About() {
  usePageSeo({
    title: "关于我们 - 蜜桃成熟时电影",
    description: "了解蜜桃成熟时电影的网站定位、发展愿景与联系方式，我们专注高清电影资源展示与剧情介绍。",
    keywords: "关于我们,蜜桃成熟时电影,影视平台介绍,联系方式,电影资源平台",
  });
  useReveal(["about"]);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden bg-secondary-950">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20view%20of%20a%20cozy%20art%20house%20cinema%20lobby%20with%20warm%20peach%20and%20amber%20lighting%2C%20vintage%20film%20posters%20on%20walls%2C%20soft%20glow%20and%20bokeh%2C%20artistic%20atmosphere%2C%20no%20text&width=1600&height=900&seq=about-hero&orientation=landscape"
          alt="蜜桃成熟时电影"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950 via-secondary-950/70 to-secondary-950/40"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-4 md:px-8">
          <span className="inline-flex items-center gap-2 self-start px-3 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-label mb-5">
            <i className="ri-information-line"></i>
            关于我们
          </span>
          <h1 className="font-heading font-black text-3xl md:text-5xl text-background-50 max-w-3xl leading-tight">
            一个为电影爱好者而生的内容平台
          </h1>
          <p className="mt-4 text-background-200 text-sm md:text-base max-w-2xl leading-7">
            我们相信，了解一部电影的过程，本身就该是一次愉悦的体验。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        {/* Intro */}
        <section className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="reveal">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">网站简介</h2>
            <div className="space-y-4 text-sm md:text-base leading-8 text-foreground-700">
              <p>
                「蜜桃成熟时电影」是一个专注高清电影资源展示与剧情介绍的影视内容平台。
                我们围绕电影资源与剧情介绍两大主题，为电影爱好者及寻求电影信息的观众提供丰富的展示内容与详细的剧情说明。
              </p>
              <p>
                平台按题材、地区、年份等多个维度对电影进行整理，配合关键词搜索与排序功能，
                让你可以快速浏览、筛选并发现感兴趣的作品，也方便你在观影前后了解剧情与看点。
              </p>
              <p className="text-foreground-600">
                本站所有电影数据均为演示内容，仅用于功能展示，不含真实播放、下载、会员及支付等相关业务。
              </p>
            </div>
          </div>
          <div className="reveal grid grid-cols-2 gap-4">
            <div className="rounded-lg overflow-hidden h-56 md:h-72 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Stack%20of%20vintage%20film%20reels%20and%20a%20projector%20in%20warm%20amber%20light%2C%20nostalgic%20cinema%20props%2C%20soft%20shadow%2C%20artistic%20still%20life%2C%20peach%20tone%2C%20no%20text&width=700&height=900&seq=about-img-01&orientation=portrait"
                alt="电影胶片与放映机"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-lg overflow-hidden h-56 md:h-72 bg-background-200 mt-8">
              <img
                src="https://readdy.ai/api/search-image?query=Person%20writing%20film%20review%20notes%20in%20a%20notebook%20beside%20a%20cup%20of%20tea%2C%20warm%20cozy%20desk%20light%2C%20artistic%20editorial%20photography%2C%20peach%20tone%2C%20no%20text&width=700&height=900&seq=about-img-02&orientation=portrait"
                alt="影评笔记"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="mt-16 md:mt-20">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6 md:mb-8">我们的坚持</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="reveal bg-background-50 border border-background-200 rounded-lg p-5">
                <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading font-semibold text-foreground-950 mb-2">{v.title}</h3>
                <p className="text-sm leading-7 text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-16 md:mt-20">
          <div className="reveal rounded-lg bg-secondary-900 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 md:p-12">
                <span className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.2em] text-primary-300 uppercase mb-3">
                  <span className="w-5 h-px bg-primary-400"></span>
                  Contact
                </span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-background-50 mb-3">联系方式</h2>
                <p className="text-sm text-background-300 leading-7 mb-8">
                  如果你对内容有任何建议，或希望与我们进行内容合作，欢迎通过以下方式联系我们。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {CONTACTS.map((c) => (
                    <div key={c.label} className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-lg bg-secondary-800 text-accent-400 flex items-center justify-center shrink-0">
                        <i className={`${c.icon} text-lg`}></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs text-background-400 font-label">{c.label}</p>
                        <p className="text-sm text-background-100 mt-0.5 break-words">{c.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-8">
                  <a href="#" aria-label="微博" className="w-10 h-10 rounded-full bg-secondary-800 flex items-center justify-center text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer">
                    <i className="ri-weibo-fill"></i>
                  </a>
                  <a href="#" aria-label="微信" className="w-10 h-10 rounded-full bg-secondary-800 flex items-center justify-center text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer">
                    <i className="ri-wechat-fill"></i>
                  </a>
                  <a href="#" aria-label="哔哩哔哩" className="w-10 h-10 rounded-full bg-secondary-800 flex items-center justify-center text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer">
                    <i className="ri-bilibili-fill"></i>
                  </a>
                  <a href="#" aria-label="豆瓣" className="w-10 h-10 rounded-full bg-secondary-800 flex items-center justify-center text-background-200 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer">
                    <i className="ri-douban-fill"></i>
                  </a>
                </div>
              </div>
              <div className="relative min-h-[280px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20inviting%20creative%20studio%20space%20with%20soft%20peach%20and%20amber%20lighting%2C%20plants%20and%20minimal%20desks%2C%20artistic%20editorial%20interior%20photography%2C%20no%20text&width=1000&height=900&seq=about-contact&orientation=landscape"
                  alt="联系蜜桃成熟时电影"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-secondary-900 via-secondary-900/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Copyright */}
        <section className="mt-16 md:mt-20 mb-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-500"></i>
              版权声明
            </h2>
            <p className="text-sm leading-7 text-foreground-600">
              © 2026 蜜桃成熟时电影 · 保留所有权利。本站所有文字、图片及电影数据均为演示内容，
              仅用于产品功能展示，不构成任何真实影视资源的提供或推荐。如需引用站内内容，请注明来源。
              如对内容有任何疑问，欢迎通过上方联系方式与我们沟通。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-apps-2-line"></i>
                浏览电影分类
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-label hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}